import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import {
  ChartContainer,
  ChartsGrid,
  ChartTitle,
  PageSection,
  StatCard,
  StatChange,
  StatIcon,
  StatLabel,
  StatsGrid,
  StatValue,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  ViewAllButton,
  TableActions,
  ChartWrapper,
  LoadingState,
  ChartLockOverlay,
  ChartBlur,
  ChartLockIcon,
  ChartBackground,
  CenteredContainer,
  StatusOption,
} from "./AdminDashboardPage.styles";

import {
  FiTrendingUp,
  FiPackage,
  FiArrowUp,
  FiEye,
  FiDollarSign,
  FiUsers,
  FiArrowDown,
  FiLock,
  FiFileText,
  FiClock,
  FiActivity,
  FiCheck,
  FiXCircle,
} from "react-icons/fi";

import AdminContainer from "../../../components/utils/AdminContainer";
import { formatDate, formatPrice } from "../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { getOrders } from "../../../store/slices/orders/asyncThunks";
import { selectOrders } from "../../../store/slices/orders/slice";
import { getTopBandsByRevenue } from "../../../store/slices/bands/asyncThunks";
import { selectBandsByRevenue } from "../../../store/slices/bands/slice";
import { selectMonthlyRevenueByPeriod } from "../../../store/slices/revenue/slice";
import { getMonthlyRevenueByPeriod } from "../../../store/slices/revenue/asyncThunks";
import { getDetailedSiteAnalytics } from "../../../store/slices/analytics/asyncThunks";
import { selectDetailedSiteAnalytics } from "../../../store/slices/analytics/slice";
import { selectUser } from "../../../store/slices/auth/slice";
import { toast } from "../../../components/modern-ui/sonner";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const location = useLocation();

  const {
    totalRevenue,
    thisMonthRevenue,
    lastMonthRevenue,
    loading: ordersLoading,
    data: ordersData,
  } = useSelector(selectOrders);

  const { data: topBandsData, loading: getTopBandsLoading } =
    useSelector(selectBandsByRevenue);

  const {
    data: monthlyRevenueByPeriod,
    loading: monthlyRevenueByPeriodLoading,
  } = useSelector(selectMonthlyRevenueByPeriod);

  const { data: detailedSiteAnalytics, loading: detailedSiteAnalyticsLoading } =
    useSelector(selectDetailedSiteAnalytics);

  const totalRevenueEur = totalRevenue / 117;
  const monthlyRevenueEur = thisMonthRevenue / 117;
  const totalProducts = detailedSiteAnalytics?.products?.total || 0;
  const registeredBands = detailedSiteAnalytics?.bands?.total || 0;

  const monthChange = (() => {
    if (lastMonthRevenue === 0) {
      // Ako je prošli mesec bio 0, a ovaj mesec ima prihod
      return thisMonthRevenue > 0 ? 100 : 0;
    }
    // Normalna kalkulacija procenta promene
    return ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;
  })();

  const getMonthChangeText = () => {
    if (lastMonthRevenue === 0) {
      return thisMonthRevenue > 0 ? "Novi prihod ovog meseca!" : "Nema prihoda";
    }

    const change =
      ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;
    return `${Math.abs(change).toFixed(0)}% od prošlog meseca`;
  };

  const isMonthPositive = monthChange >= 0;

  useEffect(() => {
    if (!ordersLoading && !ordersData.length)
      dispatch(getOrders({ page: 1, limit: 5 }));
  }, []);

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Na čekanju";
      case "processing":
        return "U obradi";
      case "shipped":
        return "Poslano";
      case "delivered":
        return "Dostavljeno";
      case "canceled":
        return "Otkazano";
      default:
        return status;
    }
  };

  const getOrderStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FiClock />;
      case "processing":
        return <FiActivity />;
      case "delivered":
        return <FiCheck />;
      case "canceled":
        return <FiXCircle />;
      default:
        return <FiClock />;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#1a1a1a",
            padding: "10px",
            border: "1px solid #ff6b35",
            borderRadius: "8px",
            color: "#ffffff",
          }}
        >
          <p>{`${label}: ${formatPrice(payload[0].value)}`}</p>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#1a1a1a",
            padding: "10px",
            border: "1px solid #ff6b35",
            borderRadius: "8px",
            color: "#ffffff",
          }}
        >
          <p>{`${payload[0].payload.bandName}: ${formatPrice(
            payload[0].payload.totalRevenue
          )}`}</p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      // Proveri da li dolazi sa login stranice
      if (location.state?.fromLogin) {
        toast.success("Dobrodošli na Admin Panel!");
        // Očisti state da se ne prikaže ponovo
        window.history.replaceState({}, document.title);
      }
    }
  }, [user, location.state]);

  useEffect(() => {
    dispatch(getTopBandsByRevenue())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));

    // Automatski generiši parametre za trenutnu godinu
    const currentYear = new Date().getFullYear();

    dispatch(
      getMonthlyRevenueByPeriod({
        startYear: currentYear,
        startMonth: 1,
        endYear: currentYear,
        endMonth: 12,
      })
    )
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));

    dispatch(getDetailedSiteAnalytics())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
  }, []);

  return (
    <AdminContainer>
      <PageSection>
        <StatsGrid>
          <StatCard>
            <StatIcon>
              <FiTrendingUp />
            </StatIcon>
            <StatValue>{formatPrice(totalRevenueEur, true)}</StatValue>
            <StatLabel $eur>{formatPrice(totalRevenue)}</StatLabel>
            <StatLabel>Ukupan Prihod</StatLabel>
            <StatLabel style={{ fontSize: "0.8rem", color: "#ff6b35" }}>
              Naš deo:{" "}
              {formatPrice(
                (detailedSiteAnalytics?.platformRevenue?.total || 0) / 117,
                true
              )}{" "}
              ={" "}
              {formatPrice(detailedSiteAnalytics?.platformRevenue?.total || 0)}
            </StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <FiDollarSign />
            </StatIcon>
            <StatValue>{formatPrice(monthlyRevenueEur, true)}</StatValue>
            <StatLabel $eur>{formatPrice(thisMonthRevenue)}</StatLabel>
            <StatLabel>Prihod Ovog Meseca</StatLabel>
            <StatLabel style={{ fontSize: "0.8rem", color: "#ff6b35" }}>
              Naš deo:{" "}
              {formatPrice(
                (detailedSiteAnalytics?.platformRevenue?.currentMonth || 0) /
                  117,
                true
              )}{" "}
              ={" "}
              {formatPrice(
                detailedSiteAnalytics?.platformRevenue?.currentMonth || 0
              )}
            </StatLabel>
            <StatChange $positive={isMonthPositive}>
              <CenteredContainer>
                {isMonthPositive ? <FiArrowUp /> : <FiArrowDown />}
              </CenteredContainer>
              {getMonthChangeText()}
            </StatChange>
          </StatCard>

          <StatCard>
            <StatIcon>
              <FiPackage />
            </StatIcon>
            {detailedSiteAnalyticsLoading ? (
              <LoadingState style={{ height: "auto", minHeight: "80px" }}>
                <l-line-spinner
                  size="24"
                  stroke="2.5"
                  speed="1"
                  color="#ff6b35"
                />
              </LoadingState>
            ) : (
              <>
                <StatValue>{totalProducts}</StatValue>
                <StatLabel>Ukupno Proizvoda</StatLabel>
                <StatChange
                  $positive={detailedSiteAnalytics?.products?.trend === "up"}
                >
                  <CenteredContainer>
                    {detailedSiteAnalytics?.products?.trend === "up" ? (
                      <FiArrowUp />
                    ) : (
                      <FiArrowDown />
                    )}
                  </CenteredContainer>
                  {detailedSiteAnalytics?.products?.trend === "up"
                    ? `+${detailedSiteAnalytics?.products?.growthPercentage}%`
                    : `-${detailedSiteAnalytics?.products?.growthPercentage}%`}{" "}
                  novih proizvoda
                </StatChange>
              </>
            )}
          </StatCard>

          <StatCard>
            <StatIcon>
              <FiUsers />
            </StatIcon>
            {detailedSiteAnalyticsLoading ? (
              <LoadingState style={{ height: "auto", minHeight: "80px" }}>
                <l-line-spinner
                  size="24"
                  stroke="2.5"
                  speed="1"
                  color="#ff6b35"
                />
              </LoadingState>
            ) : (
              <>
                <StatValue>{registeredBands}</StatValue>
                <StatLabel>Registrovanih Bendova</StatLabel>
                <StatChange
                  $positive={detailedSiteAnalytics?.bands?.trend === "up"}
                >
                  <CenteredContainer>
                    {detailedSiteAnalytics?.bands?.trend === "up" ? (
                      <FiArrowUp />
                    ) : (
                      <FiArrowDown />
                    )}
                  </CenteredContainer>
                  {detailedSiteAnalytics?.bands?.trend === "up"
                    ? `+${detailedSiteAnalytics?.bands?.growthPercentage}%`
                    : `-${detailedSiteAnalytics?.bands?.growthPercentage}%`}{" "}
                  novih bendova
                </StatChange>
              </>
            )}
          </StatCard>
        </StatsGrid>

        <ChartsGrid>
          <ChartContainer>
            <ChartTitle>Prodaja po Mesecima</ChartTitle>
            <ChartWrapper>
              {monthlyRevenueByPeriodLoading ? (
                <LoadingState>
                  <l-line-spinner
                    size="32"
                    stroke="2.5"
                    speed="1"
                    color="#ff6b35"
                  />
                  Učitavanje podataka o prodaji...
                </LoadingState>
              ) : monthlyRevenueByPeriod.length >= 3 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={monthlyRevenueByPeriod.map((item) => ({
                      month: new Date(0, item.month - 1).toLocaleDateString(
                        "sr-Latn-RS",
                        { month: "short" }
                      ),
                      revenue: item.totalRevenue,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="month" stroke="#cccccc" fontSize={12} />
                    <YAxis
                      stroke="#cccccc"
                      fontSize={12}
                      tickFormatter={(value) => `${value / 1000}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#ff6b35"
                      strokeWidth={3}
                      dot={{ fill: "#ff6b35", strokeWidth: 2, r: 6 }}
                      activeDot={{ r: 8, stroke: "#ff6b35", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <ChartLockOverlay>
                  <ChartBackground />
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={monthlyRevenueByPeriod.map((item) => ({
                        month: new Date(0, item.month - 1).toLocaleDateString(
                          "sr-Latn-RS",
                          { month: "short" }
                        ),
                        revenue: item.totalRevenue,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="month" stroke="#cccccc" fontSize={12} />
                      <YAxis
                        stroke="#cccccc"
                        fontSize={12}
                        tickFormatter={(value) => `${value / 1000}k`}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#ff6b35"
                        strokeWidth={3}
                        dot={{ fill: "#ff6b35", strokeWidth: 2, r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <ChartBlur />
                  <ChartLockIcon>
                    <FiLock />
                    <div className="lock-text">
                      Potrebno je najmanje{" "}
                      <span className="highlight">3 meseca</span> podataka
                      <br />
                      za prikazivanje grafa
                    </div>
                  </ChartLockIcon>
                </ChartLockOverlay>
              )}
            </ChartWrapper>
          </ChartContainer>

          <ChartContainer>
            <ChartTitle>Top Bendovi</ChartTitle>
            <ChartWrapper>
              {getTopBandsLoading ? (
                <LoadingState>
                  <l-line-spinner
                    size="32"
                    stroke="2.5"
                    speed="1"
                    color="#ff6b35"
                  />
                  Učitavanje podataka...
                </LoadingState>
              ) : topBandsData.length >= 3 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <defs>
                      <filter
                        id="dropShadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="2"
                          dy="2"
                          stdDeviation="3"
                          floodColor="rgba(0,0,0,0.3)"
                        />
                      </filter>
                    </defs>
                    <Pie
                      data={topBandsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="totalRevenue"
                      filter="url(#dropShadow)"
                    >
                      {topBandsData.map((entry, index) => {
                        const colors = [
                          { color: "#e74c3c", shadow: "#e74c3c40" },
                          { color: "#3498db", shadow: "#3498db40" },
                          { color: "#9b59b6", shadow: "#9b59b640" },
                          { color: "#e67e22", shadow: "#e67e2240" },
                          { color: "#27ae60", shadow: "#27ae6040" },
                          { color: "#16a085", shadow: "#16a08540" },
                          { color: "#f39c12", shadow: "#f39c1240" },
                        ];
                        const colorIndex = index % colors.length;
                        const bandColors = colors[colorIndex];

                        return (
                          <Cell key={`cell-${index}`} fill={bandColors.color} />
                        );
                      })}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <ChartLockOverlay>
                  <ChartBackground />
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <defs>
                        <filter
                          id="dropShadowBlurred"
                          x="-50%"
                          y="-50%"
                          width="200%"
                          height="200%"
                        >
                          <feDropShadow
                            dx="2"
                            dy="2"
                            stdDeviation="3"
                            floodColor="rgba(0,0,0,0.3)"
                          />
                        </filter>
                      </defs>
                      <Pie
                        data={topBandsData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={2}
                        dataKey="totalRevenue"
                        filter="url(#dropShadowBlurred)"
                      >
                        {topBandsData.map((entry, index) => {
                          const colors = [
                            { color: "#e74c3c", shadow: "#e74c3c40" },
                            { color: "#3498db", shadow: "#3498db40" },
                            { color: "#9b59b6", shadow: "#9b59b640" },
                            { color: "#e67e22", shadow: "#e67e2240" },
                            { color: "#27ae60", shadow: "#27ae6040" },
                            { color: "#16a085", shadow: "#16a08540" },
                            { color: "#f39c12", shadow: "#f39c1240" },
                          ];
                          const colorIndex = index % colors.length;
                          const bandColors = colors[colorIndex];

                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={bandColors.color}
                            />
                          );
                        })}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <ChartBlur />
                  <ChartLockIcon>
                    <FiLock />
                    <div className="lock-text">
                      Potrebno je najmanje{" "}
                      <span className="highlight">3 benda</span> podataka
                      <br />
                      za prikazivanje grafa
                    </div>
                  </ChartLockIcon>
                </ChartLockOverlay>
              )}
            </ChartWrapper>
          </ChartContainer>
        </ChartsGrid>

        <TableContainer>
          <TableActions>
            <ChartTitle>Poslednje Porudžbine</ChartTitle>

            <ViewAllButton onClick={() => navigate("/admin-panel/porudzbine")}>
              <FiEye />
              Vidi Sve
            </ViewAllButton>
          </TableActions>

          <Table>
            <thead>
              <tr>
                <TableHeader>Broj Porudžbine</TableHeader>
                <TableHeader>Kupac</TableHeader>
                <TableHeader>Cena</TableHeader>
                <TableHeader>Datum</TableHeader>
                <TableHeader>Status</TableHeader>
              </tr>
            </thead>
            <tbody>
              {ordersData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#cccccc",
                    }}
                  >
                    <div style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>
                      <FiFileText size={32} color="#666" />
                    </div>
                    <div>Trenutno nema porudžbina</div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        marginTop: "0.5rem",
                        opacity: 0.7,
                      }}
                    >
                      Nove porudžbine će se prikazati ovde
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                ordersData
                  .map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.orderNumber}</TableCell>
                      <TableCell>
                        {order.firstName} {order.lastName}
                      </TableCell>
                      <TableCell>{formatPrice(order.itemsPrice)}</TableCell>
                      <TableCell>{formatDate(order.createdAt)}</TableCell>
                      <TableCell>
                        <StatusOption $status={order.status}>
                          {getOrderStatusIcon(order.status)}
                          {getStatusText(order.status)}
                        </StatusOption>
                      </TableCell>
                    </TableRow>
                  ))
                  .slice(0, 5)
              )}
            </tbody>
          </Table>
        </TableContainer>
      </PageSection>
    </AdminContainer>
  );
};

export default AdminDashboard;
